<script lang="ts">
  /**
   * A segment is a TOOLTIP target, not a link — there is no click handler here and
   * never was in either app. The web app's copy said `cursor-pointer`, which promises
   * a navigation that does not happen; the extension's said `cursor-help`, which is
   * what the thing actually does. Keeping the honest one.
   */
  import { quadMeta } from './quadrant';
  import type { RoleJourneySegment } from './member';
  import RoleIcon from './RoleIcon.svelte';
  import ChartTooltip from './ChartTooltip.svelte';

  // A member's contributor-role journey over their tenure: consecutive segments
  // (oldest → newest), each a role held for some duration, ending on the current
  // role we show everywhere. Rendered as a single rounded proportional bar; each
  // segment carries its role icon (+ duration in the non-dense size) and, on hover,
  // a styled tooltip (the shared ChartTooltip used across the insights charts) with
  // the role, what it means, the span and the contribution count. `dense` slims it
  // for the narrow contexts (members table, hovercard, reactivation list).
  let {
    journey,
    dense = false,
  }: { journey: RoleJourneySegment[]; dense?: boolean } = $props();

  function dur(d: number): string {
    if (d < 60) return `${d}d`;
    if (d < 365) return `${Math.round(d / 30)}mo`;
    return `${(d / 365).toFixed(1)}y`;
  }

  // Segment widths as percentages that ALWAYS sum to 100: give each a visible
  // floor, then split the remainder by day-share. Fixes the old flex-grow +
  // min-width + negative-margin math, which overflowed the bar with many segments.
  const widths = $derived.by(() => {
    const days = journey.map((s) => Math.max(s.days, 1));
    const total = days.reduce((a, b) => a + b, 0) || 1;
    const n = journey.length;
    const floor = n > 0 ? Math.min(100 / n, 8) : 0;
    const free = 100 - floor * n;
    return days.map((d) => floor + free * (d / total));
  });

  // --- hover tooltip (fixed, so it escapes the bar / any parent overflow) ---
  type Hover = { seg: RoleJourneySegment; i: number; x: number; y: number };
  let hover = $state<Hover | null>(null);
  function enter(e: MouseEvent, seg: RoleJourneySegment, i: number): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { seg, i, x: r.left + r.width / 2, y: r.top };
  }
</script>

{#if journey.length}
  <div
    class="flex w-full overflow-hidden rounded-full bg-base-200 {dense ? 'h-5' : 'h-7'}"
    role="img"
    aria-label="Contributor role journey, oldest to newest"
  >
    {#each journey as seg, i (i)}
      {@const meta = quadMeta(seg.quadrant)}
      <div
        class="flex cursor-help items-center justify-center gap-1 overflow-hidden text-white transition-[filter] hover:brightness-110"
        style="width: {widths[i]}%; background: {meta.color}"
        onmouseenter={(e) => enter(e, seg, i)}
        onmouseleave={() => (hover = null)}
        role="presentation"
      >
        <RoleIcon name={meta.icon} size={dense ? 10 : 13} />
        {#if !dense}<span class="truncate text-[0.65rem] font-semibold tabular-nums">{dur(seg.days)}</span>{/if}
      </div>
    {/each}
  </div>
{:else}
  <p class="text-xs text-base-content/62">Not enough history to chart a journey yet.</p>
{/if}

{#if hover}
  {@const h = hover}
  {@const meta = quadMeta(h.seg.quadrant)}
  {@const current = h.i === journey.length - 1}
  <ChartTooltip x={h.x} y={h.y} class="w-60 px-3 py-2.5">
    <div class="flex items-center gap-1.5">
      <span class="inline-flex h-4 w-4 items-center justify-center rounded-full text-white" style:background={meta.color}>
        <RoleIcon name={meta.icon} size={11} />
      </span>
      <span class="font-mono text-[0.7rem] font-semibold">{meta.label}</span>
      {#if current}
        <span class="ml-auto rounded bg-primary/15 px-1.5 py-0.5 font-mono text-[0.58rem] font-semibold text-primary">current</span>
      {/if}
    </div>
    <p class="mt-1.5 text-xs leading-snug text-base-content/70">{meta.tip}</p>
    <div class="mt-2 flex items-center justify-between gap-3 border-t hair pt-2 font-mono text-[0.65rem] text-base-content/72">
      <span>{h.seg.start} → {h.seg.end}</span>
      <span class="tabular-nums">{dur(h.seg.days)}</span>
    </div>
    <div class="mt-0.5 font-mono text-[0.65rem] text-base-content/72">
      <span class="font-semibold text-base-content">{h.seg.activity.toLocaleString()}</span> contribution{h.seg.activity === 1 ? '' : 's'} in this role
    </div>
  </ChartTooltip>
{/if}
